/**
 * bankImport/parseBankCsv — Profil-getriebener generischer CSV-Parser
 * (architecture-bank-reconciliation.md §5.2/§9.1, BR-2 v2 GESTRAFFT).
 *
 * **Abweichung von §3 „papaparse":** Kein `papaparse`-Import — stattdessen
 * ein ~50-Zeilen-RFC4180-Mini-Parser (`parseDelimitedText`). Begründung
 * (s. changelog-parser.md): `papaparse` ist aktuell KEINE Dependency von
 * `frontend-migrated`; ein Hinzufügen hätte ein `npm install` erzwungen,
 * das mit dem zeitgleich im selben Worktree arbeitenden bankMatching-Coder
 * hätte kollidieren können (geteiltes `node_modules`/`package-lock.json`).
 * §3 sieht diesen Fallback für genau diesen Fall ausdrücklich vor
 * („Typing-Probleme: … Fallback: eigener Mini-CSV-Parser"). Die
 * unterstützten Bank-CSV-Formate sind strukturell einfach genug (ein
 * Delimiter, `"`-Quoting mit Verdopplung als Escape) für einen kleinen,
 * volltestbaren Parser ohne neue schwere Dependency.
 *
 * Zeilen-Invariante (Härtung v2, s. changelog-parser.md): jede gebaute Zeile
 * durchläuft zusätzlich `assertValidParsedStatementRow` (`direction`⟺
 * Vorzeichen, §6.2), bevor sie als gültig gilt — analog zu `parseCamt053`.
 *
 * @module bankImport/parseBankCsv
 */

import { centsFromDecimalString } from './money';
import {
  assertValidParsedStatementRow,
  BankImportError,
  type CsvProfileShape,
  type ParsedStatement,
  type ParsedStatementRow,
} from './types';
import type { BankCsvColumnMap } from '../../types/bankTypes';

// ─────────────────────────────────────────────────────────────────────────
// Mini-RFC4180-Parser (Delimiter-getrieben, Quoting mit `"…""…"`-Escape)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Zerlegt Delimiter-separierten Text in Zeilen/Zellen. Unterstützt
 * quotierte Felder (auch mit eingebettetem Delimiter/Zeilenumbruch) und
 * `""`-Escape für ein literales `"`. Normalisiert CRLF/LF.
 */
function parseDelimitedText(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\r') {
      i += 1; // CRLF-Normalisierung: \r ignorieren, \n schließt die Zeile.
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

function isBlankRow(row: string[]): boolean {
  return row.every((cell) => cell.trim().length === 0);
}

// ─────────────────────────────────────────────────────────────────────────
// Spalten-Auflösung
// ─────────────────────────────────────────────────────────────────────────

interface ColumnIndexes {
  bookingDate: number;
  valueDate: number | null;
  amount: number | null;
  amountWithSign: { amount: number; signColumn: number; debitValues: string[] } | null;
  debitCredit: { debit: number; credit: number } | null;
  counterpartyName: number | null;
  counterpartyIban: number | null;
  purpose: number[];
  acctSvcrRef: number | null;
  endToEndId: number | null;
  mandateRef: number | null;
  bankTxCode: number | null;
}

function buildColumnIndex(headerRow: string[], columnMap: BankCsvColumnMap): ColumnIndexes {
  const header = headerRow.map((h) => h.trim());

  const required = (name: string): number => {
    const idx = header.indexOf(name);
    if (idx === -1) {
      throw new BankImportError('format_unknown', `CSV-Header enthält Pflichtspalte "${name}" nicht.`);
    }
    return idx;
  };
  const optional = (name: string | undefined): number | null => {
    if (name === undefined) return null;
    const idx = header.indexOf(name);
    return idx === -1 ? null : idx;
  };

  const bookingDate = required(columnMap.bookingDate);
  const purpose = columnMap.purpose.map((name) => required(name));

  let amount: number | null = null;
  let amountWithSign: { amount: number; signColumn: number; debitValues: string[] } | null = null;
  let debitCredit: { debit: number; credit: number } | null = null;

  if (columnMap.amount !== undefined) {
    amount = required(columnMap.amount);
  } else if (columnMap.amountWithSign !== undefined) {
    amountWithSign = {
      amount: required(columnMap.amountWithSign.amount),
      signColumn: required(columnMap.amountWithSign.signColumn),
      debitValues: columnMap.amountWithSign.debitValues,
    };
  } else if (columnMap.debitCredit !== undefined) {
    debitCredit = {
      debit: required(columnMap.debitCredit.debit),
      credit: required(columnMap.debitCredit.credit),
    };
  } else {
    throw new BankImportError('format_unknown', 'Profil definiert keine Betrags-Spalten-Variante.');
  }

  return {
    bookingDate,
    valueDate: optional(columnMap.valueDate),
    amount,
    amountWithSign,
    debitCredit,
    counterpartyName: optional(columnMap.counterpartyName),
    counterpartyIban: optional(columnMap.counterpartyIban),
    purpose,
    acctSvcrRef: optional(columnMap.acctSvcrRef),
    endToEndId: optional(columnMap.endToEndId),
    mandateRef: optional(columnMap.mandateRef),
    bankTxCode: optional(columnMap.bankTxCode),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Datums-/Betrags-/Zeilen-Aufbau
// ─────────────────────────────────────────────────────────────────────────

function parseDateToIso(raw: string, format: CsvProfileShape['dateFormat']): string {
  const trimmed = raw.trim();
  switch (format) {
    case 'YYYY-MM-DD': {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        throw new Error(`Datum nicht im Format YYYY-MM-DD: "${trimmed}".`);
      }
      return trimmed;
    }
    case 'DD.MM.YYYY': {
      const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(trimmed);
      if (!m) throw new Error(`Datum nicht im Format DD.MM.YYYY: "${trimmed}".`);
      return `${m[3]}-${m[2]}-${m[1]}`;
    }
    case 'DD.MM.YY': {
      const m = /^(\d{2})\.(\d{2})\.(\d{2})$/.exec(trimmed);
      if (!m) throw new Error(`Datum nicht im Format DD.MM.YY: "${trimmed}".`);
      const year = Number(m[3]) >= 70 ? `19${m[3]}` : `20${m[3]}`;
      return `${year}-${m[2]}-${m[1]}`;
    }
    case 'MM/DD/YYYY': {
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
      if (!m) throw new Error(`Datum nicht im Format MM/DD/YYYY: "${trimmed}".`);
      return `${m[3]}-${m[1]}-${m[2]}`;
    }
    default:
      throw new Error(`Unbekanntes Datumsformat: "${format}".`);
  }
}

function resolveAmountCents(dataRow: string[], colIndex: ColumnIndexes, decimalStyle: 'de' | 'en'): number {
  const cell = (idx: number): string => (dataRow[idx] ?? '').trim();

  if (colIndex.amount !== null) {
    return centsFromDecimalString(cell(colIndex.amount), decimalStyle);
  }
  if (colIndex.amountWithSign !== null) {
    const magnitude = Math.abs(centsFromDecimalString(cell(colIndex.amountWithSign.amount), decimalStyle));
    const signRaw = cell(colIndex.amountWithSign.signColumn).toUpperCase();
    const isDebit = colIndex.amountWithSign.debitValues.some((v) => v.trim().toUpperCase() === signRaw);
    return isDebit ? -magnitude : magnitude;
  }
  if (colIndex.debitCredit !== null) {
    const debitRaw = cell(colIndex.debitCredit.debit);
    const creditRaw = cell(colIndex.debitCredit.credit);
    const hasDebit = debitRaw.length > 0;
    const hasCredit = creditRaw.length > 0;
    if (hasDebit === hasCredit) {
      throw new Error(`Soll-/Haben-Spalten widersprüchlich befüllt (Soll="${debitRaw}", Haben="${creditRaw}").`);
    }
    const magnitude = Math.abs(centsFromDecimalString(hasDebit ? debitRaw : creditRaw, decimalStyle));
    return hasDebit ? -magnitude : magnitude;
  }
  throw new Error('Keine Betrags-Spalten-Variante im Profil konfiguriert.');
}

/** Uppercase ohne Spaces — lokal dupliziert, s. Begründung in `dedup.ts` (keine bankMatching-Abhängigkeit). */
function normalizeIbanLocal(raw: string): string | null {
  const cleaned = raw.toUpperCase().replace(/\s+/g, '');
  return cleaned.length > 0 ? cleaned : null;
}

function buildRowFromCsv(dataRow: string[], colIndex: ColumnIndexes, profile: CsvProfileShape): ParsedStatementRow {
  const cell = (idx: number | null): string => (idx === null ? '' : (dataRow[idx] ?? '').trim());

  const bookingDate = parseDateToIso(cell(colIndex.bookingDate), profile.dateFormat);
  const valueDateRaw = cell(colIndex.valueDate);
  const valueDate = valueDateRaw.length > 0 ? parseDateToIso(valueDateRaw, profile.dateFormat) : null;

  const amountCents = resolveAmountCents(dataRow, colIndex, profile.decimalStyle);
  if (amountCents === 0) {
    // 0,00 ist weder eindeutig Gutschrift noch Belastung (direction⟺Vorzeichen-
    // Invariante, §6.2) — als Zeilenfehler behandeln statt zu raten.
    throw new Error('Betrag 0,00 — keine eindeutige Richtung (Gutschrift/Belastung) ableitbar.');
  }
  const direction: 'credit' | 'debit' = amountCents < 0 ? 'debit' : 'credit';

  const purpose = colIndex.purpose
    .map((idx) => cell(idx))
    .filter((s) => s.length > 0)
    .join(' ');

  const counterpartyIbanRaw = cell(colIndex.counterpartyIban);

  return {
    bookingDate,
    valueDate,
    amountCents,
    direction,
    currency: 'EUR',
    counterpartyName: cell(colIndex.counterpartyName) || null,
    counterpartyIban: counterpartyIbanRaw.length > 0 ? normalizeIbanLocal(counterpartyIbanRaw) : null,
    counterpartyBic: null,
    purpose,
    endToEndId: cell(colIndex.endToEndId) || null,
    mandateRef: cell(colIndex.mandateRef) || null,
    creditorId: null,
    bankTxCode: cell(colIndex.bankTxCode) || null,
    acctSvcrRef: cell(colIndex.acctSvcrRef) || null,
    rawSource: dataRow.join(profile.delimiter).slice(0, 2000),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Öffentliche API
// ─────────────────────────────────────────────────────────────────────────

/**
 * Parst einen bereits dekodierten CSV-Text anhand eines Profils
 * (User-Mapping-Wizard-Ergebnis oder built-in, §5.2) in ein `ParsedStatement`.
 *
 * Robust gegen Fuß-/Leerzeilen: komplett leere Zeilen werden vor der
 * Header-/Datenzeilen-Zählung entfernt; nicht-leere, aber strukturell
 * unpassende Fußzeilen (z.B. eine Saldo-Zusammenfassung mit zu wenigen
 * Spalten) landen als Zeilenfehler in `ParsedStatement.errors`, brechen den
 * Import aber nicht ab.
 *
 * @param text - Bereits dekodierter CSV-Text (s. `decodeStatementFile`, BOM-frei).
 * @param profile - Spalten-Mapping + Format-Optionen (User- oder built-in-Profil).
 * @throws {BankImportError} `'format_unknown'` bei fehlenden Pflichtspalten
 *   im Header oder fehlender Betrags-Spalten-Variante.
 */
export function parseBankCsv(text: string, profile: CsvProfileShape): ParsedStatement {
  const allRows = parseDelimitedText(text, profile.delimiter);
  const meaningfulRows = allRows.filter((row) => !isBlankRow(row));

  if (meaningfulRows.length <= profile.skipLeadingRows) {
    throw new BankImportError('no_transactions', 'CSV enthält nach skipLeadingRows keine Header-/Datenzeile.');
  }

  const headerRow = meaningfulRows[profile.skipLeadingRows];
  const dataRows = meaningfulRows.slice(profile.skipLeadingRows + 1);
  const colIndex = buildColumnIndex(headerRow, profile.columnMap);

  const rows: ParsedStatementRow[] = [];
  const errors: Array<{ rawLine: string; reason: string }> = [];

  for (const dataRow of dataRows) {
    try {
      const row = buildRowFromCsv(dataRow, colIndex, profile);
      assertValidParsedStatementRow(row);
      rows.push(row);
    } catch (err) {
      errors.push({ rawLine: dataRow.join(profile.delimiter), reason: (err as Error).message });
    }
  }

  return {
    accountIban: null,
    openingBalanceCents: null,
    closingBalanceCents: null,
    closingBalanceDate: null,
    rows,
    errors,
  };
}
